import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import { TagItem, addGamesToTag } from "store/gameCenter/tags/slice";
import {
  CollectionItem,
  addGamesToCollection,
} from "store/gameCenter/collections/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import SearchInput from "components/inputs/SearchInput/SearchInput";

import { Modal } from "components/Modal/Modal";
import styles from "./GameActionModal.module.scss";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

const AddGameModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const geoList = useAppSelector(selectFilteredGeo);
  const addGameModalType = useAppSelector(
    (store) => store.modals.addGameModalType,
  );
  const selectedItems = useAppSelector((store) => store.games.selectedItems);

  const [type, setType] = useState<string>();
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [activeGeo, setActiveGeo] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");
  const [items, setItems] = useState<
    { value: number; label: string; img?: string; isActive: boolean }[]
  >([]);
  const [itemsFull, setItemsFull] = useState<
    { value: number; label: string; img?: string; isActive: boolean }[]
  >([]);
  const [selectedItem, setSelectedItem] = useState<number>();

  useEffect(() => {
    setType(addGameModalType ?? "collection");
  }, [addGameModalType]);

  const getItems = async () => {
    try {
      const result = await instance.get(
        `${type === "collection" ? apiRoutes.collectionsList : apiRoutes.tagsList}?limit=500&offset=0`,
      );
      const array = result.data.items.map((el: CollectionItem | TagItem) => {
        return {
          value: el.id,
          label: el.title,
          img: el.image,
          isActive: el.isActive,
        };
      });
      setItems(array);
      setItemsFull(array);
    } catch (error) {
      console.log(error, "error");
    }
    setIsFetched(true);
  };

  useEffect(() => {
    if (type) {
      getItems();
    }
  }, [type]);

  useEffect(() => {
    const result = itemsFull.filter((el) =>
      el.label?.toLowerCase().includes(search?.toLowerCase()),
    );
    setItems(result);
  }, [search, itemsFull]);

  const handleGeoClick = (e: string) => {
    if (activeGeo.includes(e)) {
      setActiveGeo(activeGeo.filter((el) => el !== e));
    } else {
      setActiveGeo([...activeGeo, e]);
    }
  };

  const handleCloseClick = () => {
    dispatch(closeModal());
  };

  const handleItemClick = (id: number) => {
    if (selectedItem === id) {
      setSelectedItem(undefined);
    } else {
      setSelectedItem(id);
    }
  };

  const handleApply = async () => {
    if (!activeGeo.length) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("selectGeo"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }
    if (!selectedItem) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("selectCollection"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }
    if (type === "collection") {
      const result = await dispatch(
        addGamesToCollection({
          collection: selectedItem ?? 0,
          gameIds: selectedItems,
          geos: activeGeo,
        }),
      );
      if (result.payload === true) {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("gamesAddedSuccess"),
            type: NotificationTypes.success,
          }),
        );
        handleCloseClick();
      } else {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("addingGamesError"),
            type: NotificationTypes.error,
          }),
        );
      }
    } else {
      const result = await dispatch(
        addGamesToTag({
          tag: selectedItem ?? 0,
          gameIds: selectedItems,
          geos: activeGeo,
        }),
      );
      if (result.payload === true) {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("gamesAddedSuccess"),
            type: NotificationTypes.success,
          }),
        );
        handleCloseClick();
      } else {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("addingGamesError"),
            type: NotificationTypes.error,
          }),
        );
      }
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header className={styles.addToTournamentTitle}>
        {type === "collection" ? t("gamesAddToCollection") : t("gamesAddToTag")}
      </Modal.Header>

      {isFetched && (
        <Modal.Tabs
          isGeoTabs
          tabs={geoList}
          activeTab={activeGeo}
          onClick={handleGeoClick}
          isSmallModifier
        />
      )}

      <Modal.Content className={styles.addGameModalContentWrapper}>
        {isFetched ? (
          <div className="add-game-modal-content">
            <div className="game-modal-search">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e)}
                placeholder={t("searchYourEntity")}
                isSmall
              />
            </div>

            <div className="add-game-modal-list games-order-list">
              <div className="item header">
                <div className="id">ID</div>
                <div className="img">{t("image")}</div>
                <div className="title">{t("name").toUpperCase()}</div>
                <div className="label">{t("active").toUpperCase()}</div>
              </div>
              {items.map((el) => (
                <div
                  key={el.value}
                  onClick={() => handleItemClick(el.value)}
                  className={`item ${selectedItem === el.value ? "active" : ""}`}
                >
                  <div className="id">{el.value}</div>
                  <div className="img">
                    <img src={el.img} alt="" />
                  </div>
                  <div className="title">{el.label}</div>
                  <div className="label">
                    <div
                      className={`active-label ${el.isActive ? "active" : ""}`}
                    >
                      {el.isActive ? "On" : "Off"}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length === 0 && <div className="empty">{t("notFound")}</div>}
          </div>
        ) : (
          <ModalTabSkeleton />
        )}
      </Modal.Content>

      <Modal.Footer>
        <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
        <Button isPrimary handleClick={handleApply} txt={t("apply")} />
      </Modal.Footer>
    </Modal>
  );
};

export default AddGameModal;
